import {
  type CreateTrackerTemplateDto,
  TrackerTemplatesService,
  type TrackerTemplateResponseDto,
  type UpdateTrackerTemplateDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';

import { TRACKER_TEMPLATE_QUERY_KEYS } from './query-keys';

/** Save a tracker as a reusable template (snapshot happens server-side). */
export function useSaveTrackerTemplateMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTrackerTemplateDto) => {
      const response = await TrackerTemplatesService.create({ client: workspaceHttp, body });
      if (!response.data) throw new Error('tracker template create returned an empty body');
      return response.data;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<TrackerTemplateResponseDto[]>(
        TRACKER_TEMPLATE_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => (prev ? [created, ...prev] : [created]),
      );
    },
  });
}

export function useUpdateTrackerTemplateMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      body,
    }: {
      templateId: string;
      body: UpdateTrackerTemplateDto;
    }) => {
      const response = await TrackerTemplatesService.update({
        client: workspaceHttp,
        path: { templateId },
        body,
      });
      if (!response.data) throw new Error('tracker template update returned an empty body');
      return response.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<TrackerTemplateResponseDto[]>(
        TRACKER_TEMPLATE_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => prev?.map((t) => (t.id === updated.id ? updated : t)),
      );
    },
  });
}

export function useDeleteTrackerTemplateMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId }: { templateId: string }) => {
      await TrackerTemplatesService.delete({ client: workspaceHttp, path: { templateId } });
    },
    onSuccess: (_data, { templateId }) => {
      queryClient.setQueryData<TrackerTemplateResponseDto[]>(
        TRACKER_TEMPLATE_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => prev?.filter((t) => t.id !== templateId),
      );
    },
  });
}
